import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Spacer,
  AspectRatio,
  Image,
  HStack,
  Heading,
} from '@chakra-ui/react';

import Content from '~/containers/Content';
import PageContainer from '~/containers/PageContainer';

import StoreLastPreOrderTimeNotice from '~/components/StoreLastPreOrderTimeNotice';
import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterReturnNotice from '~/components/FooterReturnNotice';
import FooterLoginSection from '~/components/FooterLoginSection';
import EmptySection from '~/components/EmptySection';
import BackwardBar from '~/components/BackwardBar';
import LocationBar from '~/components/LocationBar';
import GoTopButton from '~/components/GoTopButton';

import { CategoriesMenu, StoreMealList, FixedHeaderSection, StoreStatusBar } from './components';

import { storeAvailableItemList as storeAvailableItemListAtom } from '~/pages/Store/hooks/useStoreMenuList';
import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import storeCategoryAtom from '~/recoil/atom/storeCategory';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import storeInfoAtom from '~/recoil/atom/storeInfo';
import platformAtom from '~/recoil/atom/platform';
import authAtom from '~/recoil/atom/auth';
import { useRecoilValue, useRecoilState } from 'recoil';

import { useLocation, useNavigate } from 'react-router-dom';

import useCartId from '~/hooks/useCartId';

import * as fp from 'lodash/fp';

import getScheduleTimeString from '~/utils/getScheduleTimeString';
import checkValidPreOrderTime from '~/utils/checkValidPreOrderTime';
import isNotInHoliday from '~/utils/isNotInHoliday';
import isInOperatingTime from '~/utils/isInOperatingTime';
import defaultImage from '~/assets/images/defaultImage.png';

import { CartType } from '~/types/cart';

export default function Store() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useRecoilValue(authAtom);
  const storeInfo = useRecoilValue(storeInfoAtom);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const platform = useRecoilValue(platformAtom);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const [storeCategory, setStoreCategory] = useRecoilState(storeCategoryAtom);

  const storeAvailableItemList = useRecoilValue(storeAvailableItemListAtom);

  const { cartId, cartType } = useCartId();

  const categoriesList = useMemo(() => {
    return storeAvailableItemList.map(({ id, name }) => ({ id, name }));
  }, [storeAvailableItemList]);

  const [selectedCategory, setSelectedCategory] = useState({ id: '', name: '' });

  const isPlatformContainStore =
    currentPickMealPoint.stores.includes(storeInfo.id) &&
    platform.displayStores.includes(storeInfo.id);

  useEffect(() => {
    if (fp.gt(categoriesList.length, 0) && storeCategory) {
      setSelectedCategory(
        categoriesList.find(({ id }) => fp.eq(id, storeCategory)) || { id: '', name: '' },
      );
      setStoreCategory('');
    }
  }, [categoriesList, storeCategory]);

  const isInStoreOperatingTime = useMemo(
    () =>
      isInOperatingTime(storeInfo.preOrderSetting.schedule, pickMealAt) &&
      isNotInHoliday(storeInfo.officialHoliday, pickMealAt),
    [pickMealAt, storeInfo.preOrderSetting.schedule, storeInfo.officialHoliday],
  );

  // 檢查最後結單時間是否超過現在時間
  const [isValidPreOrderTime, lastPreOrderTime] = useMemo(() => {
    const { schedule, beforeHours, afterHours } = storeInfo.preOrderSetting;
    const now = new Date();

    return checkValidPreOrderTime(schedule, afterHours, beforeHours, pickMealAt, now);
  }, [storeInfo.preOrderSetting, pickMealAt]);

  return (
    <PageContainer
      hideNavBar
      hideHeader
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => {
              // @ts-ignore
              if (location.state?.previous) {
                // @ts-ignore
                navigate(location.state?.previous);
              } else {
                navigate('/');
              }
            }}
          />
          <LocationBar
            rightHandComponent={
              <Text fontSize="xs" color="white">
                你所選的取餐地點
              </Text>
            }
          />
          <Box px={4} bgColor="white">
            <Content>
              <HStack py={2}>
                <AspectRatio w="35px" ratio={1 / 1} borderRadius="full">
                  <Image borderRadius="full" src={storeInfo.logo || defaultImage} />
                </AspectRatio>
                <Heading size="sm">{storeInfo.name}</Heading>
              </HStack>
            </Content>
          </Box>
          {!isPlatformContainStore && (
            <Box px={4} bgColor="danger.200">
              <Content py={2}>
                <Text color="danger.600" fontSize="xs" fontWeight="bold">
                  此餐廳沒有與現在的取餐點合作
                </Text>
              </Content>
            </Box>
          )}
        </>
      }
    >
      <FixedHeaderSection />

      <Content>
        <AspectRatio ratio={3 / 2}>
          <Image src={storeInfo.banners[0] || defaultImage} />
        </AspectRatio>
      </Content>

      <Box bg="white">
        <Content px={4} py={2}>
          <VStack align="stretch">
            <StoreStatusBar
              scheduleTimeString={getScheduleTimeString(
                storeInfo.preOrderSetting.schedule,
                storeInfo.officialHoliday,
                pickMealAt,
              )}
              onNavigate={() => navigate('./info')}
            />
            {isInStoreOperatingTime ? (
              <StoreLastPreOrderTimeNotice
                isValidTime={isValidPreOrderTime}
                time={lastPreOrderTime}
              />
            ) : (
              <VStack align="stretch" bgColor="danger.200" px={2} py={4} borderRadius="5px">
                <Text fontSize="xs" fontWeight="bold" color="danger.600">
                  未營業
                </Text>
                <Text fontSize="xs" color="danger.600">
                  您所選取的取餐時間不符合店家營業時間
                </Text>
              </VStack>
            )}
          </VStack>
        </Content>
        <Content minH="60vh">
          <VStack align="stretch" p={4} spacing={8}>
            {fp.eq(storeAvailableItemList.length, 0) ? (
              <EmptySection content="目前無可選擇餐點" />
            ) : (
              <>
                <CategoriesMenu
                  activeCategory={selectedCategory}
                  categoriesList={categoriesList}
                  onChangeCategory={(value) => setSelectedCategory(value)}
                />
                <StoreMealList
                  selectedCategory={selectedCategory}
                  categoriesWithItems={storeAvailableItemList}
                />
              </>
            )}
          </VStack>
        </Content>

        <Box position="sticky" bottom="0">
          <Box px={4}>
            <Content py={2}>
              <Flex>
                <Spacer />
                <GoTopButton />
              </Flex>
            </Content>
          </Box>

          <FooterReturnNotice />
          <>
            {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
              <FooterLoginSection />
            ) : (
              <FooterCartStatusBar
                onNavigate={() =>
                  navigate(fp.eq(cartType, CartType.CART_GROUP) ? `/group/${cartId}/cart` : '/cart')
                }
              />
            )}
          </>
        </Box>
      </Box>
    </PageContainer>
  );
}
